<template>
  <div class="pa-4">
    <!-- Breadcrumb and back button -->
    <v-row class="align-center mb-4">
      <v-col cols="12" md="10">
        <Breadcrump :crumbs="crumbs" />
      </v-col>
      <v-col cols="12" md="2" class="d-flex justify-end">
        <v-btn @click="$router.go(-1)" fab small dark color="#1976d2">
          <v-icon dark>mdi-arrow-left</v-icon>
        </v-btn>
      </v-col>
    </v-row>

    <!-- Device Summary Card -->
    <v-row>
      <v-col cols="12">
        <v-card class="mb-4 elevation-3 v-main-card" :loading="loading">
          <!-- Device Header -->
          <v-card-title
            class="d-flex justify-space-between align-center flex-wrap ga-2"
          >
            <div class="d-flex align-center flex-wrap">
              <v-icon large color="primary" class="ml-3">mdi-devices</v-icon>
              <div>
                <div class="font-weight-semibold ml-3">{{ device.name }}</div>
              </div>
              <div class="ml-3">
                <div class="font-weight-medium small-text">
                  <span class="grey--text">کد سخت‌افزاری: </span>
                  <span>{{ device.hardwareCode }}</span>
                </div>
              </div>
              <div class="ml-3">
                <div class="font-weight-medium small-text">
                  <span class="grey--text">نوع دستگاه: </span>
                  <span>{{ device.deviceType ?? "-" }}</span>
                </div>
              </div>
              <div class="ml-3">
                <div class="font-weight-medium small-text">
                  <span class="grey--text">تاریخ/ساعت دستگاه: </span>
                  <span>{{ formattedDeviceDateTime }}</span>
                </div>
              </div>
            </div>
            <div class="d-flex ga-1 justify-center align-center flex-wrap">
              <v-chip
                small
                :color="device.isBranded ? 'success' : 'error'"
                text-color="white"
              >
                {{ device.isBranded ? "برند دارد" : "برند ندارد" }}
              </v-chip>
              <v-btn
                v-if="turnOffPerm"
                small
                dark
                color="yellow darken-2"
                class="mr-2"
                text-color="white"
                @click="confirmRestart(deviceId, device.name)"
              >
                <v-icon left small>mdi-restart</v-icon>
                راه‌اندازی مجدد
              </v-btn>
              <v-btn
                v-if="turnOffPerm"
                small
                dark
                color="red"
                class="mr-2"
                text-color="white"
                @click="confirmTurnOff(deviceId, device.name)"
              >
                <v-icon left small>mdi-power</v-icon>
                خاموش کردن
              </v-btn>
            </div>
          </v-card-title>

          <v-divider></v-divider>

          <v-card-text>
            <v-row
              ><!-- Usage Report Card -->
              <v-col cols="12" md="6" lg="4" v-if="usagePerm">
                <v-card outlined class="pa-3 h-100 d-flex flex-column">
                  <div class="d-flex align-center mb-3">
                    <v-icon color="info" class="ml-2">mdi-chart-bar</v-icon>
                    <span class="font-weight-bold">استفاده از دستگاه</span>
                  </div>

                  <v-card-text class="pa-0">
                    <v-simple-table dense fixed-header>
                      <thead>
                        <tr>
                          <th class="text-right">وضعیت</th>
                          <th class="text-right">زمان رویداد</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr
                          v-for="(item, index) in device.usageReportData"
                          :key="index"
                        >
                          <td class="extra-small-text">
                            {{ item.state }}
                          </td>
                          <td class="extra-small-text">
                            {{ item.eventTime }}
                          </td>
                        </tr>
                        <tr v-if="usageReportDataLoading">
                          <td colspan="2" class="text-center py-4">
                            <span class="grey--text">درحال بارگذاری...</span>
                          </td>
                        </tr>
                        <tr
                          v-else-if="
                            !device.usageReportData ||
                              device.usageReportData.length === 0
                          "
                        >
                          <td colspan="2" class="text-center py-4">
                            <v-icon small color="grey lighten-1" class="ml-1"
                              >mdi-alert-circle-outline</v-icon
                            >
                            <span class="grey--text"
                              >هیچ داده‌ای وجود ندارد</span
                            >
                          </td>
                        </tr>
                      </tbody>
                    </v-simple-table>
                  </v-card-text>

                  <div class="d-flex fill-height pa-1"></div>
                  <div class="d-flex flex-column ga-2">
                    <v-btn
                      v-if="hasPerm('Devices_Report')"
                      block
                      outlined
                      color="primary"
                      small
                      @click="navigateTo('deviceReport')"
                    >
                      <v-icon left small>mdi-chart-bar</v-icon>
                      گزارش استفاده از دستگاه
                    </v-btn>
                    <v-btn
                      v-if="hasPerm('LockerCurrentState_Show')"
                      block
                      outlined
                      small
                      color="cyan"
                      @click="navigateTo('deviceState')"
                    >
                      <v-icon left small>mdi-monitor-cellphone</v-icon>
                      وضعیت فعلی دستگاه
                    </v-btn>
                  </div>
                </v-card>
              </v-col>
              <!-- Device Status Flags Card with Button -->
              <v-col cols="12" md="6" lg="4">
                <v-card outlined class="pa-3 h-100 d-flex flex-column">
                  <div class="d-flex align-center mb-3">
                    <v-icon color="grey" class="ml-2">mdi-alert-circle</v-icon>
                    <span class="font-weight-bold">وضعیت دستگاه</span>
                  </div>

                  <div class="d-flex flex-column ga-3 extra-small-text">
                    <!-- <div class="d-flex align-center justify-space-between mt-2">
                      <div class="d-flex align-center">
                        <v-icon
                          :color="getShutDownStatusColor(device.shutDownFlag)"
                          size="24"
                          class="ml-2"
                        >
                          {{
                            device.isOnline || !device.shutDownFlag
                              ? "mdi-power"
                              : "mdi-power-off"
                          }}
                        </v-icon>
                        <span class="font-weight-medium">وضعیت خاموشی</span>
                      </div>
                      <v-chip
                        :color="getShutDownStatusColor(device.shutDownFlag)"
                        dark
                        small
                      >
                        {{ getShutDownStatusText(device.shutDownFlag) }}
                      </v-chip>
                    </div> 

                    <v-divider></v-divider> -->

                    <div class="d-flex align-center justify-space-between">
                      <div class="d-flex align-center">
                        <v-icon
                          :color="
                            !device.hasCaseDoor
                              ? 'info'
                              : device.caseDoorFlag
                              ? 'error'
                              : 'success'
                          "
                          size="24"
                          class="ml-2"
                        >
                          {{
                            !device.hasCaseDoor
                              ? "mdi-door"
                              : device.caseDoorFlag
                              ? "mdi-door-open"
                              : "mdi-door-closed"
                          }}
                        </v-icon>
                        <span class="font-weight-medium">وضعیت درب کیس</span>
                      </div>
                      <v-chip
                        :color="
                          !device.hasCaseDoor
                            ? 'info'
                            : device.caseDoorFlag
                            ? 'red'
                            : 'green'
                        "
                        dark
                        small
                      >
                        {{
                          !device.hasCaseDoor
                            ? "غیرفعال"
                            : device.caseDoorFlag
                            ? "باز"
                            : "بسته"
                        }}
                      </v-chip>
                    </div>

                    <v-divider></v-divider>

                    <div class="d-flex align-center justify-space-between">
                      <div class="d-flex align-center">
                        <v-icon
                          :color="getOnlineStatusColor(device.isOnline)"
                          size="24"
                          class="ml-2"
                        >
                          {{ device.isOnline ? "mdi-wifi" : "mdi-wifi-off" }}
                        </v-icon>
                        <span class="font-weight-medium">وضعیت ارتباط</span>
                      </div>
                      <v-chip
                        :color="getOnlineStatusColor(device.isOnline)"
                        dark
                        small
                      >
                        {{ getOnlineStatusText(device.isOnline) }}
                      </v-chip>
                    </div>

                    <v-divider></v-divider>

                    <div class="d-flex align-center justify-space-between">
                      <div class="d-flex align-center">
                        <v-icon
                          :color="getLightBoxStatusColor(device)"
                          size="24"
                          class="ml-2"
                        >
                          {{ getLightBoxIcon(device) }}
                        </v-icon>
                        <span class="font-weight-medium">وضعیت لایت‌باکس</span>
                      </div>
                      <v-chip
                        :color="getLightBoxStatusColor(device)"
                        dark
                        small
                      >
                        {{ getLightBoxStatusText(device) }}
                      </v-chip>
                    </div>

                    <v-divider></v-divider>

                    <div class="d-flex align-center justify-space-between">
                      <div class="d-flex align-center">
                        <v-icon
                          :color="getScreenColor(device)"
                          size="24"
                          class="ml-2"
                        >
                          {{ getScreenIcon(device) }}
                        </v-icon>
                        <span class="font-weight-medium">وضعیت اسکرین</span>
                      </div>
                      <v-chip :color="getScreenColor(device)" dark small>
                        {{ getScreenText(device) }}
                      </v-chip>
                    </div>

                    <v-divider></v-divider>

                    <div class="d-flex align-center justify-space-between">
                      <div class="d-flex align-center">
                        <v-icon
                          :color="getdatabaseHealthColor(device.databaseHealth)"
                          size="24"
                          class="ml-2"
                        >
                          {{ getdatabaseHealthIcon(device.databaseHealth) }}
                        </v-icon>
                        <span class="font-weight-medium">دیتابیس</span>
                      </div>
                      <v-chip
                        :color="getdatabaseHealthColor(device.databaseHealth)"
                        dark
                        small
                      >
                        {{ getdatabaseHealthStatusText(device.databaseHealth) }}
                      </v-chip>
                    </div>
                  </div>

                  <!-- <v-alert
                    v-if="device.shutDownFlag || device.caseDoorFlag"
                    type="warning"
                    dense
                    class="mt-3 mb-0"
                  >
                    <div class="d-flex align-center">
                      <span>
                        {{ device.shutDownFlag ? "دستگاه خاموش است. " : "" }}
                        {{ device.caseDoorFlag ? "درب کیس باز است." : "" }}
                      </span>
                    </div>
                  </v-alert> -->
                  <div class="d-flex fill-height pa-1"></div>
                  <v-btn
                    v-if="hasPerm('DeviceHistory_Show')"
                    block
                    outlined
                    small
                    color="primary"
                    class="mt-3"
                    @click="navigateTo('deviceTimeline')"
                  >
                    <v-icon left small>mdi-history</v-icon>
                    تاریخچه دستگاه
                  </v-btn>
                </v-card>
              </v-col>
              <!-- Memory Status Card with Button -->
              <v-col cols="12" md="6" lg="4">
                <v-card outlined class="pa-3 h-100 d-flex flex-column">
                  <div class="d-flex align-center mb-3">
                    <v-icon color="info" class="ml-2">mdi-memory</v-icon>
                    <span class="font-weight-bold">وضعیت حافظه</span>
                  </div>
                  <div class="text-center">
                    <v-progress-circular
                      :value="getMemoryPercentage(device)"
                      :size="100"
                      :width="10"
                      :color="getMemoryColor(device)"
                      class="mb-3"
                    >
                      <div class="d-flex items-center align-center flex-column">
                        <span class="font-weight-semibold"
                          >{{ getMemoryPercentage(device) }}%</span
                        >
                        <span class="font-weight-semibold">مصرف شده</span>
                      </div>
                    </v-progress-circular>
                    <div class="my-2 extra-small-text">
                      <div>
                        <strong>حافظه کل:</strong>
                        {{ formatMemory(device.totalMemory) }}
                      </div>
                      <div>
                        <strong>حافظه خالی:</strong>
                        {{ formatMemory(device.freeMemory) }}
                      </div>
                    </div>
                  </div>
                  <div class="d-flex fill-height pa-1"></div>

                  <div class="d-flex flex-column ga-2">
                    <v-btn
                      v-if="clearDbPerm"
                      block
                      outlined
                      color="error"
                      small
                      @click="confirmClearDatabase(deviceId, device.name)"
                    >
                      <v-icon left small>mdi-database-remove</v-icon>
                      پاک کردن دیتابیس
                    </v-btn>

                    <v-btn
                      v-if="clearImagePerm"
                      block
                      outlined
                      color="warning"
                      small
                      @click="confirmClearImages(deviceId, device.name)"
                    >
                      <v-icon left small>mdi-image-remove</v-icon>
                      پاک کردن تصاویر
                    </v-btn>
                  </div>
                </v-card>
              </v-col>

              <!-- Working Hours Card with Button -->
              <v-col cols="12" md="6" lg="4">
                <v-card outlined class="pa-3 h-100 d-flex flex-column">
                  <div class="d-flex align-center mb-3">
                    <v-icon color="info" class="ml-2">mdi-clock-outline</v-icon>
                    <span class="font-weight-bold">ساعت کاری</span>
                  </div>
                  <template v-if="!device.isFullTime">
                    <div class="d-flex align-center justify-space-between mb-2">
                      <div class="d-flex align-center">
                        <v-icon small color="success" class="ml-2"
                          >mdi-play-circle</v-icon
                        >
                        <span class="small-text">شروع:</span>
                      </div>
                      <span class="font-weight-bold">{{
                        device.startTime
                      }}</span>
                    </div>

                    <div class="d-flex align-center justify-space-between mb-2">
                      <div class="d-flex align-center">
                        <v-icon small color="error" class="ml-2"
                          >mdi-stop-circle</v-icon
                        >
                        <span class="small-text">پایان:</span>
                      </div>
                      <span class="font-weight-bold">{{ device.endTime }}</span>
                    </div>
                  </template>

                  <template v-else>
                    <div class="text-center py-2">
                      <v-icon x-large color="info">mdi-clock-outline</v-icon>
                      <div class="mt-1 font-weight-semibold">
                        ۲۴ ساعته (تمام وقت)
                      </div>
                    </div>
                  </template>

                  <v-divider class="my-2"></v-divider>

                  <div class="d-flex align-center justify-space-between mb-3">
                    <div class="d-flex align-center">
                      <v-icon color="info" small class="ml-2">
                        {{
                          isWithinWorkingHours
                            ? "mdi-check-circle"
                            : "mdi-alert-circle"
                        }}
                      </v-icon>
                      <span class="small-text">وضعیت:</span>
                    </div>
                    <v-chip
                      :color="isWithinWorkingHours ? 'success' : 'warning'"
                      dark
                      small
                    >
                      {{
                        isWithinWorkingHours
                          ? "در ساعت کاری"
                          : "خارج از ساعت کاری"
                      }}
                    </v-chip>
                  </div>
                  <div class="d-flex fill-height pa-1"></div>
                  <v-btn
                    v-if="hasPerm('CodeActivity_Show')"
                    block
                    outlined
                    small
                    color="primary"
                    @click="navigateTo('deviceCodeActivity')"
                  >
                    <v-icon left small>mdi-code-tags</v-icon>
                    کدهای فعالیت
                  </v-btn>
                </v-card>
              </v-col>

              <!-- Last Update Status Card with Buttons -->
              <v-col v-if="otaLogPerm" cols="12" md="6" lg="4">
                <v-card outlined class="pa-3 h-100 d-flex flex-column">
                  <div class="d-flex align-center mb-3">
                    <v-icon
                      :color="getUpdateIconColor(device.latestLog)"
                      class="ml-2"
                    >
                      {{ getUpdateIcon(device.latestLog) }}
                    </v-icon>
                    <span class="font-weight-bold">آخرین آپدیت</span>
                  </div>

                  <div v-if="device.latestLog">
                    <div class="mb-2">
                      <v-chip
                        :color="getUpdateChipColor(device.latestLog.state)"
                        dark
                        small
                      >
                        {{ getLogStatusText(device.latestLog.state) }}
                      </v-chip>
                    </div>
                    <div
                      class="caption grey--text d-flex flex-column ga-1 mt-1"
                    >
                      <div>
                        <strong>نسخه:</strong>
                        {{ device.latestLog.version || "_" }}
                      </div>
                      <div>
                        <strong>تاریخ:</strong>
                        {{ formatDate(device.latestLog.createdAt) }}
                      </div>
                      <div>
                        <strong>پیام:</strong> {{ device.latestLog.message }}
                      </div>
                    </div>
                  </div>
                  <div v-else class="text-center grey--text mb-3">
                    <v-icon>mdi-alert-circle-outline</v-icon>
                    <div>هیچ آپدیتی ثبت نشده است</div>
                  </div>
                  <div class="d-flex fill-height pa-1"></div>
                  <div class="d-flex flex-column ga-2">
                    <v-btn
                      v-if="hasPerm('OtaList_ShowLogs')"
                      block
                      outlined
                      color="info"
                      small
                      @click="navigateTo('deviceOTALogs')"
                    >
                      <v-icon left small>mdi-history</v-icon>
                      گزارش نسخه های دستگاه
                    </v-btn>
                    <v-btn
                      v-if="hasPerm('OtaList_Assignment')"
                      block
                      outlined
                      color="cyan"
                      small
                      @click="navigateTo('deviceOTADeviceForm')"
                    >
                      <v-icon left small>mdi-update</v-icon>
                      آپدیت دستگاه
                    </v-btn>
                  </div>
                </v-card>
              </v-col>

              <!-- SMS Last Status Card with Buttons -->
              <v-col v-if="deviceSmsPerm" cols="12" md="6" lg="4">
                <v-card outlined class="pa-3 h-100 d-flex flex-column">
                  <div class="d-flex align-center mb-3">
                    <v-icon
                      :color="getSmsStatusColor(device.smsLastState?.isSent)"
                      class="ml-2"
                    >
                      mdi-message-text
                    </v-icon>
                    <span class="font-weight-bold">آخرین وضعیت پیامک</span>
                  </div>

                  <div v-if="device.smsLastState && device.sendSmsStatus">
                    <div class="mb-2">
                      <v-chip
                        :color="getSmsStatusColor(device.smsLastState.isSent)"
                        dark
                        small
                      >
                        {{ getSmsStatusText(device.smsLastState.isSent) }}
                      </v-chip>
                    </div>
                    <div
                      class="caption grey--text d-flex flex-column ga-1 mt-1"
                    >
                      <div>
                        <strong>تاریخ:</strong>
                        {{ formatDate(device.smsLastState.createdAt) }}
                      </div>
                      <div v-if="device.smsLastState.messageText">
                        <strong>متن پیام:</strong>
                        {{ device.smsLastState.messageText }}
                      </div>
                      <div v-if="device.smsLastState.errorText">
                        <strong>متن خطا:</strong>
                        {{ device.smsLastState.errorText }}
                      </div>
                    </div>
                  </div>
                  <div v-else class="text-center grey--text mb-3">
                    <v-icon>mdi-message-alert-outline</v-icon>
                    <div>
                      {{
                        !device.sendSmsStatus &&
                        !device.sendWinGameSmsStatus &&
                        !device.sendLoseGameSmsStatus
                          ? "ارسال پیامک فعال نیست"
                          : "هیچ پیامکی ثبت نشده است"
                      }}
                    </div>
                  </div>
                  <v-divider class="my-2"></v-divider>
                  <div class="d-flex flex-column ga-2 extra-small-text">
                    <div class="d-flex align-center justify-space-between mt-2">
                      <div class="d-flex align-center">
                        <v-icon
                          :color="device.sendSmsStatus ? 'success' : 'info'"
                          small
                          class="ml-2"
                        >
                          mdi-message
                        </v-icon>
                        <span class="small-text">پیامک تبلیغاتی:</span>
                      </div>
                      <v-chip
                        :color="device.sendSmsStatus ? 'success' : 'info'"
                        dark
                        small
                      >
                        {{ device.sendSmsStatus ? "فعال" : "غیرفعال" }}
                      </v-chip>
                    </div>
                    <div class="d-flex align-center justify-space-between mt-2">
                      <div class="d-flex align-center">
                        <v-icon :color="getGameColor(true)" small class="ml-2">
                          mdi-thumb-up
                        </v-icon>
                        <span class="small-text">پیامک برد:</span>
                      </div>
                      <v-chip :color="getGameColor(true)" dark small>
                        {{ getGameText(true) }}
                      </v-chip>
                    </div>
                    <div class="d-flex align-center justify-space-between my-2">
                      <div class="d-flex align-center">
                        <v-icon :color="getGameColor(false)" small class="ml-2">
                          mdi-thumb-down
                        </v-icon>
                        <span class="small-text">پیامک باخت:</span>
                      </div>
                      <v-chip :color="getGameColor(false)" dark small>
                        {{ getGameText(false) }}
                      </v-chip>
                    </div>
                  </div>

                  <div class="d-flex fill-height pa-1"></div>
                  <div class="d-flex flex-column ga-2">
                    <v-btn
                      v-if="hasPerm('DeviceSms_List')"
                      block
                      outlined
                      color="info"
                      small
                      @click="navigateTo('deviceSms')"
                    >
                      <v-icon left small>mdi-message-text-outline</v-icon>
                      گزارش پیام‌ها </v-btn
                    ><v-btn
                      v-if="hasPerm('SmsContent_View')"
                      block
                      outlined
                      color="cyan"
                      small
                      @click="navigateTo('deviceSmsPanel')"
                    >
                      <v-icon left small>mdi-message-processing</v-icon>
                      پنل پیامکی
                    </v-btn>
                  </div>
                </v-card>
              </v-col>

              <!-- Operations Card with additional buttons -->
              <v-col cols="12" md="6" lg="4" v-if="operationPerm">
                <v-card outlined class="pa-3 h-100 d-flex flex-column">
                  <div class="d-flex align-center mb-3">
                    <v-icon color="warning" class="ml-2">mdi-tools</v-icon>
                    <span class="font-weight-bold">عملیات و گزارشات</span>
                  </div>

                  <div class="d-flex fill-height pa-1"></div>
                  <div class="d-flex flex-column ga-2">
                    <v-btn
                      v-if="hasPerm('Devices_ReportMultiply')"
                      block
                      outlined
                      color="primary"
                      small
                      @click="navigateTo('deviceReportMultiplier')"
                    >
                      <v-icon left small>mdi-chart-line</v-icon>
                      ضریب‌دهی گزارش
                    </v-btn>

                    <v-btn
                      v-if="hasPerm('Pins_Show')"
                      block
                      outlined
                      color="cyan"
                      small
                      @click="navigateTo('devicePin')"
                    >
                      <v-icon left small>mdi-pin</v-icon>
                      پین‌ها
                    </v-btn>

                    <v-btn
                      v-if="hasPerm(`Devices_Edit`)"
                      block
                      outlined
                      color="primary"
                      small
                      @click="navigateTo('settings')"
                    >
                      <v-icon left small>mdi-cog</v-icon>
                      تنظیمات دستگاه
                    </v-btn>
                    <v-btn
                      v-if="hasPerm(`DeviceDb_Show`)"
                      block
                      outlined
                      color="cyan"
                      small
                      @click="navigateTo('deviceDb')"
                    >
                      <v-icon left small>mdi-database-export</v-icon>
                      دیتابیس دستگاه
                    </v-btn>

                    <v-btn
                      v-if="hasPerm(`Devices_MarketingEdit`)"
                      block
                      outlined
                      color="primary"
                      small
                      @click="navigateTo('deviceMarketing')"
                    >
                      <v-icon left small>mdi-cog</v-icon>
                      تنظیمات مارکتینگ دستگاه
                    </v-btn>
                  </div>
                </v-card>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import request from "@/utils/request";
import Breadcrump from "@/components/common/Breadcrump.vue";
import moment from "moment-jalaali";
import Vue from "vue";
import { hasPermission } from "@/composables/permissions";

export default {
  name: "DeviceHealthDashboard",
  components: { Breadcrump },

  data() {
    return {
      crumbs: [
        { text: "خانه", disabled: false, href: "/" },
        { text: "مجموعه ها", disabled: false, href: "/complexes" },
        {
          text: `مجموعه ${this.$store.state.selectedComplexName || ""}`,
          disabled: false,
          href: `/complex-units/${this.$store.state.selectedComplexId}`
        },
        {
          text: this.$store.state.selectedDeviceName || "دستگاه",
          href: `/complex-devices/${this.$store.state.selectedComplexId}`,
          disabled: false
        },
        { text: "نمایش دستگاه", disabled: true }
      ],
      filter: {
        deviceId: null,
        phoneNumber: null,
        lockerNo: null,
        isSent: null,
        fromDate: null,
        toDate: null,
        deviceIds: []
      },
      device: {
        id: null,
        isBranded: false,
        hardwareCode: null,
        complexId: null,
        deviceDateTime: null,
        name: "",
        active: false,
        sendSmsStatus: false,
        sendLoseGameSmsStatus: false,
        sendWinGameSmsStatus: false,
        winGameSmsId: false,
        loseGameSmsId: false,
        deviceType: null,
        totalMemory: 0,
        freeMemory: 0,
        isOnline: false,
        lightBoxStatus: null,
        lightBoxStatusThreshold: null,
        latestLog: null,
        smsLastState: null,
        shutDownFlag: false,
        caseDoorFlag: false,
        startTime: null,
        endTime: null,
        isFullTime: null,
        usageReportData: []
      },
      usageReportDataLoading: true,
      deviceId: null,
      loading: true,
      complexId: null
    };
  },

  computed: {
    formattedDeviceDateTime() {
      if (!this.device.deviceDateTime || !this.device.isOnline) return "-";
      try {
        return moment(this.device.deviceDateTime).format(
          "jYYYY/jMM/jDD HH:mm:ss"
        );
      } catch {
        return "-";
      }
    },
    winGameStatus() {
      if (this.device.sendWinGameSmsStatus) {
        if (this.device.winGameSmsId) return 1;
        return 2;
      }
      return 3;
    },
    loseGameStatus() {
      if (this.device.sendLoseGameSmsStatus) {
        if (this.device.loseGameSmsId) return 1;
        return 2;
      }
      return 3;
    },
    isWithinWorkingHours() {
      if (
        this.device.isFullTime ||
        !this.device.startTime ||
        !this.device.endTime
      )
        return true;

      const now = new Date();
      const currentTime = now.getHours() * 60 + now.getMinutes();

      let [startHour, startMinute] = this.device.startTime
        .split(":")
        .map(Number);
      let [endHour, endMinute] = this.device.endTime.split(":").map(Number);

      let startTotal = startHour * 60 + startMinute;
      let endTotal = endHour * 60 + endMinute;

      if (endTotal <= startTotal) {
        endTotal += 24 * 60;
      }

      let currentAdjusted = currentTime;
      if (currentAdjusted < startTotal && endTotal > 24 * 60) {
        currentAdjusted += 24 * 60;
      }

      return currentAdjusted >= startTotal && currentAdjusted <= endTotal;
    },
    otaLogPerm() {
      return hasPermission("OtaList_ShowLogs");
    },
    deviceSmsPerm() {
      return hasPermission("DeviceSms_List");
    },
    turnOffPerm() {
      return hasPermission("Devices_TurnOff");
    },
    usagePerm() {
      return hasPermission("Devices_Report");
    },
    clearDbPerm() {
      return hasPermission("Devices_ClearDatabase");
    },
    clearImagePerm() {
      return hasPermission("Devices_ClearImages");
    },
    operationPerm() {
      return (
        hasPermission("Devices_Edit") ||
        hasPermission("Pins_Show") ||
        hasPermission("Devices_ReportMultiply")
      );
    }
  },

  mounted() {
    this.deviceId = this.$route.params.id;
    this.complexId =
      this.$route.params.complexId || this.$store.state.selectedComplexId;
    this.filter.deviceId = this.$route.params.id;
    this.getDevice();
    this.loadDeviceAdditionalData();
  },

  methods: {
    hasPerm(item) {
      return hasPermission(item);
    },
    navigateTo(action) {
      this.$store.commit("setSelectedDeviceName", this.device.name);

      const routes = {
        deviceState: () =>
          this.$router.push(`/device-state/${this.complexId}/${this.deviceId}`),
        deviceCodeActivity: () =>
          this.$router.push(`/device-code-activity/${this.deviceId}`),
        deviceAdvertise: () =>
          this.$router.push(`/device-advertise/${this.deviceId}`),
        deviceSmsPanel: () =>
          this.$router.push(`/device-sms-panel/${this.deviceId}`),
        deviceReportMultiplier: () =>
          this.$router.push(`/device-report-multiplier/${this.deviceId}`),
        devicePin: () => this.$router.push(`/device-pins/${this.deviceId}`),
        deviceDb: () => this.$router.push(`/device/${this.deviceId}/database`),
        settings: () => {
          this.$router.push({
            name: "EditDevice",
            params: {
              id: this.device.complexId,
              deviceId: this.deviceId
            }
          });
        },
        deviceReport: () =>
          this.$router.push(`/device-report/${this.deviceId}`),
        deviceMarketing: () =>
          this.$router.push(`/device-marketing/${this.deviceId}`),
        deviceTimeline: () =>
          this.$router.push(`/device-timeline/${this.deviceId}`),
        deviceOTADeviceForm: () =>
          this.$router.push(`/device-update/${this.deviceId}`),
        deviceOTALogs: () =>
          this.$router.push(`/device-ota-logs/${this.deviceId}`),
        deviceSms: () => this.$router.push(`/device-sms/${this.deviceId}`)
      };

      if (routes[action]) {
        routes[action]();
      }
    },

    cleanObject(obj) {
      return Object.fromEntries(
        Object.entries(obj).filter(
          ([, value]) => value !== null && value !== "" && value !== undefined
        )
      );
    },

    buildRequestParams() {
      return {
        page: 1,
        itemsPerPage: 1,
        sortBy: "createdAt",
        sortDesc: true,
        mustSort: true,
        ...this.cleanObject(this.filter)
      };
    },

    async getDevice() {
      this.loading = true;
      await request
        .get(`/device/${this.deviceId}`)
        .then(response => {
          this.device = {
            ...this.device,
            ...response.data.result,
            startTime: response.data.result.startTime,
            endTime: response.data.result.endTime
          };
        })
        .catch(() => {
          Vue.swal("خطا", "دریافت اطلاعات دستگاه با مشکل مواجه شد", "error");
        })
        .finally(() => {
          this.loading = false;
        });
    },

    async loadDeviceAdditionalData() {
      // آرایه برای نگهداری پرامیس‌ها
      const promises = [];

      // اضافه کردن پرامیس‌های شرطی
      if (this.otaLogPerm) {
        promises.push(
          request
            .get(`/deviceotalist/${this.deviceId}/latest-log`)
            .then(response => {
              this.$set(this.device, "latestLog", response.data.result || null);
            })
            .catch(() => {
              this.device.latestLog = null;
            })
        );
      }

      if (this.deviceSmsPerm) {
        const params = this.buildRequestParams();
        promises.push(
          request
            .get(`/notification/device-sms`, { params })
            .then(response => {
              this.device.smsLastState =
                response.data.result?.result[0] || null;
            })
            .catch(() => {
              this.device.smsLastState = null;
            })
        );
      }

      // پرامیس isBranded
      promises.push(
        request
          .get(`/device/${this.deviceId}/isbranded`)
          .then(response => {
            this.device.isBranded = response.data.result || false;
          })
          .catch(() => {
            this.device.isBranded = false;
          })
      );

      // پرامیس usageReportData
      const logParams = {
        page: 1,
        itemsPerPage: 3,
        sortBy: "createdAt",
        sortDesc: true
      };
      const filterQuery = Object.keys(logParams)
        .filter(x => logParams[x] !== null && logParams[x] !== undefined)
        .map(key => `${key}=${logParams[key]}`)
        .join("&");

      promises.push(
        request
          .get(
            `/deviceManagement/device-log-minimized/${this.deviceId}?${filterQuery}`
          )
          .then(response => {
            this.device.usageReportData = response.data.result?.result || [];
            this.usageReportDataLoading = false;
          })
          .catch(() => {
            this.device.usageReportData = [];
            this.usageReportDataLoading = false;
          })
      );

      try {
        await Promise.all(promises);
      } catch (error) {
        console.error("Error loading device additional data:", error);
      } finally {
        this.usageReportDataLoading = false;
      }
    },

    formatMemory(bytes) {
      if (!bytes || bytes === 0) return "0 MB";
      const formattedNumber = bytes
        .toString()
        .replace(/\B(?=(\d{3})+(?!\d))/g, ",");
      return `${formattedNumber} MB`;
    },

    getMemoryPercentage(device) {
      if (!device.totalMemory || device.totalMemory === 0) return 0;
      const used = (device.totalMemory || 0) - (device.freeMemory || 0);
      return Math.round((used / device.totalMemory) * 100);
    },

    getMemoryColor(device) {
      const percentage = this.getMemoryPercentage(device);
      if (percentage < 75) return "success";
      if (percentage < 90) return "warning";
      return "error";
    },

    getGameColor(win) {
      const status = win ? this.winGameStatus : this.loseGameStatus;
      if (status === 1) return "success";
      if (status === 2) return "warning";
      if (status === 3) return "info";
    },

    getGameText(win) {
      const status = win ? this.winGameStatus : this.loseGameStatus;
      if (status === 1) return "فعال و دارای پیامک";
      if (status === 2) return "فعال و فاقد پیامک";
      if (status === 3) return "غیرفعال";
    },

    getUpdateIcon(log) {
      if (!log) return "mdi-help-circle";
      if (log.state === 1) return "mdi-check-circle";
      if (log.state === 2) return "mdi-alert-circle";
      if (log.state === 3) return "mdi-alert-circle";
      return "mdi-update";
    },

    getUpdateIconColor(log) {
      if (!log) return "grey";
      if (log.state === 1) return "success";
      if (log.state === 2) return "warning";
      if (log.state === 3) return "error";
      return "info";
    },

    getUpdateChipColor(state) {
      if (state === 1) return "success";
      if (state === 5 || state === 6) return "warning";
      if (state === 3 || state === 2) return "error";
      return "grey";
    },

    getLogStatusText(state) {
      const statuses = {
        0: "نامشخص",
        1: "آپدیت موفق",
        2: "آپدیت ناموفق",
        3: "بازیابی ناموفق",
        4: "آپدیت دستی",
        5: "بازیابی موفق",
        6: "نسخه ناهمخوان"
      };
      return statuses[state] || "نامشخص";
    },

    getSmsStatusColor(success) {
      if (success === true) return "success";
      if (success === false) return "error";
      return "grey";
    },

    getSmsStatusText(success) {
      if (success === true) return "موفق";
      if (success === false) return "ناموفق";
      return "نامشخص";
    },

    getOnlineStatusColor(online) {
      const isInWorkingTime = this.isWithinWorkingHours;
      if (isInWorkingTime) {
        return online ? "success" : "error";
      } else {
        return online ? "error" : "success";
      }
    },

    getOnlineStatusText(online) {
      const isInWorkingTime = this.isWithinWorkingHours;
      if (isInWorkingTime) {
        return online ? "آنلاین مجاز" : "آفلاین غیرمجاز";
      } else {
        return online ? "آنلاین غیرمجاز" : "آفلاین مجاز";
      }
    },

    getShutDownStatusColor(shutdown) {
      const isInWorkingTime = this.isWithinWorkingHours;
      if (this.device.isOnline) shutdown = false;
      if (isInWorkingTime) {
        return !shutdown ? "success" : "error";
      } else {
        return !shutdown ? "error" : "success";
      }
    },

    getShutDownStatusText(shutdown) {
      const isInWorkingTime = this.isWithinWorkingHours;
      if (this.device.isOnline) shutdown = false;
      if (isInWorkingTime) {
        return !shutdown ? "روشن مجاز" : "خاموش غیرمجاز";
      } else {
        return !shutdown ? "روشن غیرمجاز" : "خاموش مجاز";
      }
    },

    getScreenColor(device) {
      if (!device.hasScreen) return "info";
      if (
        device.screenStatus > 0 &&
        device.screenStatus < 100 &&
        device.screenStatus < device.screenThreshold
      ) {
        return "error";
      }
      return "success";
    },

    getdatabaseHealthColor(state) {
      const color = {
        null: "info",
        true: "success",
        false: "error"
      };
      return color[state] || "info";
    },

    getdatabaseHealthIcon(state) {
      const color = {
        null: "mdi-database",
        true: "mdi-database-check",
        false: "mdi-database-alert"
      };
      return color[state] || "mdi-database";
    },

    getdatabaseHealthStatusText(state) {
      const status = {
        null: "نامشخص",
        true: "سالم",
        false: "خطا"
      };
      return status[state] || "نامشخص";
    },

    getLightBoxStatusColor(device) {
      if (!device.hasLightBox) return "info";
      if (
        device.lightBoxStatus > 0 &&
        device.lightBoxStatus < 100 &&
        device.lightBoxStatus < device.lightBoxStatusThreshold
      ) {
        return "error";
      }
      return "success";
    },

    getLightBoxIcon(device) {
      const color = this.getLightBoxStatusColor(device);
      if (color === "success") return "mdi-lightbulb-on";
      if (color === "error") return "mdi-lightbulb-on";
      return "mdi-lightbulb-off";
    },

    getScreenIcon(device) {
      const color = this.getScreenColor(device);
      if (color === "success") return "mdi-monitor";
      if (color === "error") return "mdi-monitor";
      return "mdi-monitor-off";
    },

    getLightBoxStatusText(device) {
      const color = this.getLightBoxStatusColor(device);
      if (color === "success") return "سالم";
      if (color === "error") return "خطا";
      return "غیرفعال";
    },

    getScreenText(device) {
      const color = this.getScreenColor(device);
      if (color === "success") return "سالم";
      if (color === "error") return "خطا";
      return "غیرفعال";
    },

    formatDate(datetime) {
      if (!datetime) return "-";
      try {
        return moment(datetime).format("jYYYY/jMM/jDD HH:mm:ss");
      } catch {
        return "-";
      }
    },

    confirmClearDatabase(deviceId, deviceName) {
      Vue.swal({
        title: `پاک کردن دیتابیس دستگاه ${deviceName}`,
        text:
          "آیا از حذف تمام اطلاعات دیتابیس این دستگاه مطمئن هستید؟ این عملیات غیرقابل بازگشت است!",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        cancelButtonColor: "#3085d6",
        confirmButtonText: "بله، پاک شود",
        cancelButtonText: "انصراف",
        width: "350px",
        padding: "1rem",
        size: "small",
        customClass: {
          popup: "small-swal",
          title: "small-swal-title",
          content: "small-swal-content",
          confirmButton: "btn-sm",
          cancelButton: "btn-sm"
        }
      }).then(result => {
        if (result.isConfirmed) {
          this.clearDatabase(deviceId);
        }
      });
    },

    async clearDatabase(deviceId) {
      try {
        await request.delete(`/device/clear-database/${deviceId}`);
        Vue.swal(
          "موفق",
          "دستور پاک کردن دیتابیس دستگاه با موفقیت ارسال شد (لطفا پس از یک دقیقه مجددا حافظه دستگاه را بررسی کنید)",
          "success"
        );
      } catch (error) {
        Vue.swal("خطا", "پاک کردن دیتابیس با مشکل مواجه شد", "error");
      }
    },

    async turnOff(deviceId) {
      try {
        await request.put(`/device/turn-off/${deviceId}`);
        Vue.swal(
          "موفق",
          "دستور خاموش کردن دستگاه با موفقیت ارسال شد (لطفا پس از یک دقیقه مجددا دستگاه را بررسی کنید)",
          "success"
        );
      } catch (error) {
        Vue.swal("خطا", "خاموش کردن دستگاه با مشکل مواجه شد", "error");
      }
    },

    confirmTurnOff(deviceId, deviceName) {
      Vue.swal({
        title: `خاموش کردن دستگاه ${deviceName}`,
        text: "آیا از خاموش کردن این دستگاه مطمئن هستید؟",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        cancelButtonColor: "#3085d6",
        confirmButtonText: "بله، خاموش شود",
        cancelButtonText: "انصراف",
        width: "350px",
        padding: "1rem",
        size: "small",
        customClass: {
          popup: "small-swal",
          title: "small-swal-title",
          content: "small-swal-content",
          confirmButton: "btn-sm",
          cancelButton: "btn-sm"
        }
      }).then(result => {
        if (result.isConfirmed) {
          this.turnOff(deviceId);
        }
      });
    },

    async restart(deviceId) {
      try {
        await request.put(`/device/restart/${deviceId}`);
        Vue.swal(
          "موفق",
          "دستور راه‌اندازی مجدد دستگاه با موفقیت ارسال شد (لطفا پس از یک دقیقه مجددا دستگاه را بررسی کنید)",
          "success"
        );
      } catch (error) {
        Vue.swal("خطا", "راه‌اندازی مجدد دستگاه با مشکل مواجه شد", "error");
      }
    },

    confirmRestart(deviceId, deviceName) {
      Vue.swal({
        title: `راه‌اندازی مجدد دستگاه ${deviceName}`,
        text: "آیا از راه‌اندازی مجدد این دستگاه مطمئن هستید؟",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        cancelButtonColor: "#3085d6",
        confirmButtonText: "بله، راه‌اندازی مجدد شود",
        cancelButtonText: "انصراف",
        width: "350px",
        padding: "1rem",
        size: "small",
        customClass: {
          popup: "small-swal",
          title: "small-swal-title",
          content: "small-swal-content",
          confirmButton: "btn-sm",
          cancelButton: "btn-sm"
        }
      }).then(result => {
        if (result.isConfirmed) {
          this.restart(deviceId);
        }
      });
    },

    confirmClearImages(deviceId, deviceName) {
      Vue.swal({
        title: `پاک کردن تصاویر دستگاه ${deviceName}`,
        text: "آیا از حذف تمام تصاویر این دستگاه مطمئن هستید؟",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        cancelButtonColor: "#3085d6",
        confirmButtonText: "بله، پاک شود",
        cancelButtonText: "انصراف",
        width: "350px",
        padding: "1rem",
        size: "small",
        customClass: {
          popup: "small-swal",
          title: "small-swal-title",
          content: "small-swal-content",
          confirmButton: "btn-sm",
          cancelButton: "btn-sm"
        }
      }).then(result => {
        if (result.isConfirmed) {
          this.clearImages(deviceId);
        }
      });
    },

    async clearImages(deviceId) {
      try {
        await request.delete(`/device/clear-images/${deviceId}`);
        Vue.swal(
          "موفق",
          "دستور پاک کردن تصاویر دستگاه با موفقیت ارسال شد (لطفا پس از یک دقیقه مجددا حافظه دستگاه را بررسی کنید)",
          "success"
        );
      } catch (error) {
        Vue.swal("خطا", "پاک کردن تصاویر با مشکل مواجه شد", "error");
      }
    }
  }
};
</script>

<style scoped>
.pa-4 {
  padding: 16px;
}

.h-100 {
  height: 100%;
}

.v-card {
  border-radius: 12px;
  transition: all 0.2s ease;
}

.v-card:hover {
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.07);
}

.caption {
  font-size: 12px;
  line-height: 1.5;
}
.ga-1 {
  gap: 4px;
}

.ga-2 {
  gap: 8px;
}

.ga-3 {
  gap: 12px;
}

.extra-small-text {
  font-size: 12px !important;
}
.small-text {
  font-size: 13px !important;
}
/* در style global یا با !important */
.swal2-popup.small-swal-popup {
  width: 320px !important;
  font-size: 12px !important;
  padding: 0.8rem !important;
}

.small-swal-title.swal2-title {
  font-size: 16px !important;
  padding: 8px 0 0 0 !important;
  margin: 0 !important;
}

.small-swal-content.swal2-html-container {
  font-size: 12px !important;
  padding: 8px !important;
  margin: 0 !important;
}

.small-swal-button.swal2-styled {
  font-size: 12px !important;
  padding: 6px 12px !important;
  margin: 4px !important;
}
</style>
